import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useMemo, Fragment, useState, useCallback } from "react";

import { Box, Stack, useTheme } from "@mui/system";
import { Card, List, Button, Divider, Tooltip, ListItem, TextField, CardHeader, Typography, CardContent, Autocomplete, ListItemText } from "@mui/material";

import { paths } from "src/routes/paths";
import { useRouter } from "src/routes/hooks";

import { useBoolean } from "src/hooks/use-boolean";
import { useOrgTenant } from "src/hooks/auth/use-org-tenant";

import { useTranslate } from "src/locales";
import { useAppDispatch } from "src/store/store";
import { showCompanyEditor } from "src/store/slices/companies/companiesSlice";
import { useGetMyCompaniesQuery } from "src/services/connections/connections.service";
import { ContactLinkType, ICompanyProfile } from "src/services/connections/connections.types";
import { useGetContactCompanyLinksQuery, useAddContactCompanyLinkMutation, useRemoveContactCompanyLinkMutation } from "src/services/contacts/contacts.service";

import Iconify from "src/components/iconify";
import EmptyContent from "src/components/empty-content";
import { ConfirmDialog } from "src/components/custom-dialog";
import TruncatedTextField from "src/components/truncate-text/truncated-text-field";

import { LoadingCardItem } from "src/sections/admin/billing/payment/payment-card-item";

type Props = {
  contactId: string;
}

const LINKED_COMPANY_QUERY_PARAMS = {
  page: 1,
  per_page: 1
};

const LINKABLE_COMPANIES_QUERY_PARAMS = {
  page: 1,
  per_page: 5
};

const LinkedCompaniesCard = ({ contactId }: Props) => {

  const { t } = useTranslate();

  const tenantType = useOrgTenant();

  const addLinkControl = useBoolean(false);

  const dispatch = useAppDispatch();

  const theme = useTheme();

  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState<ICompanyProfile[]>([]);

  const router = useRouter();

  const [addCompanyLinks, { isLoading: isAddingCompanyLinks }] = useAddContactCompanyLinkMutation()
  const [removeCompanyLink, { isLoading: isRemovingCompanyLink }] = useRemoveContactCompanyLinkMutation()

  const { data: contactCompanyLinks, isLoading: isLoadingLinks } = useGetContactCompanyLinksQuery({
    contact_id: contactId,
    params: LINKED_COMPANY_QUERY_PARAMS
  }, {
    skip: !contactId,
  });

  const { currentData: companies, isLoading: isConnectionsLoading } = useGetMyCompaniesQuery(
    {
      params: {
        ...LINKABLE_COMPANIES_QUERY_PARAMS,
        search_query: companySearchQuery
      },
    }
  );

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setCompanySearchQuery(value);
    }, 500),
    [setCompanySearchQuery]
  );

  const onAddLink = () => {
    addLinkControl.onTrue();
  }

  const onAddNewCompany = () => {
    dispatch(showCompanyEditor());
  }

  const onLinkModalClose = useCallback(() => {
    addLinkControl.onFalse();
    setSelectedCompanies([]);
    setCompanySearchQuery("");
  }, [addLinkControl, setSelectedCompanies, setCompanySearchQuery]);

  const handleAddLinks = useCallback(async () => {

    if (!selectedCompanies.length) return;

    try {
      await addCompanyLinks({
        contact_id: contactId,
        company_profiles: selectedCompanies.map((company) => company.id)
      }).unwrap();

      enqueueSnackbar(t('companies.profile.api.add_company_link.success'), { variant: 'success' });
      onLinkModalClose();

    }
    catch (error) {
      enqueueSnackbar(t('companies.profile.api.add_company_link.default_error'), { variant: 'error' });
    }
  }, [onLinkModalClose, addCompanyLinks, contactId, selectedCompanies, t]);

  const handleRemoveLink = useCallback(async (linkId: string) => {
    try {

      await removeCompanyLink({
        contact_id: contactId,
        link_id: linkId
      }).unwrap();

      enqueueSnackbar(t('companies.profile.api.remove_company_link.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('companies.profile.api.remove_company_link.default_error'), { variant: 'error' });
    }
  }, [contactId, removeCompanyLink, t]);

  const linkableCompanies = useMemo(() => {
    if (isConnectionsLoading || isLoadingLinks) return [];

    return companies?.results?.filter((company) => {
      const isAlreadyLinked = contactCompanyLinks?.results?.some((link) => link.company_profile.id === company.id);
      return !isAlreadyLinked;
    });
  }, [companies, contactCompanyLinks, isConnectionsLoading, isLoadingLinks]);

  return (
    <Card>
      <CardHeader
        title={
          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
              <Iconify
                icon="material-symbols:dataset-linked-outline" />
              <Box>{t('contacts.detail.linked_companies.title')}</Box>
            </Stack>
            {!contactCompanyLinks?.count && (
              <Button
                variant="outlined"
                size="small"
                onClick={onAddLink}
                startIcon={<Iconify icon="eva:plus-fill" />}
                sx={{
                  color: 'text.primary',
                  textTransform: 'capitalize',
                }}>
                {t('contacts.detail.linked_companies.add_link')}
              </Button>
            )}
          </Stack>
        }
      />
      <CardContent sx={{ mb: 2 }}>

        {
          isLoadingLinks &&
          <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
            <LoadingCardItem />
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        }

        {
          (!isLoadingLinks && !contactCompanyLinks?.count) && (
            <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
              <EmptyContent
                title={t('contacts.detail.linked_companies.empty')}
                description={t('contacts.detail.linked_companies.empty_description')}
                sx={{ width: '100%' }}
              />
            </Stack>
          )
        }

        {
          (!isLoadingLinks && !!contactCompanyLinks?.count) && (
            <Stack direction="column" gap={2} justifyContent="start" alignItems="start" sx={{ width: '100%' }}>
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {contactCompanyLinks?.results?.map((link) => (
                  <Fragment key={link.id}>
                    <ListItem
                      key={link.id}
                      sx={{ pt: 1 }}
                      secondaryAction={
                        <Stack direction="row" gap={1} justifyContent="center" alignItems="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              router.push(
                                paths.dashboard.companies.detail(link.company_profile.id, tenantType, link.company_profile.company_type)
                              )
                            }}
                          >
                            {t('common.view')}
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => handleRemoveLink(link.id)}
                          >
                            {t('contacts.detail.linked_companies.remove_link')}
                          </Button>
                        </Stack>
                      }>
                      <ListItemText
                        sx={{ pr: 4 }}
                        primary={
                          <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                            {
                              link.type === ContactLinkType.PRIMARY && (
                                <Tooltip title={t('companies.profile.primary_contact.tooltip')} arrow placement="top">
                                  <Iconify icon="solar:star-bold" sx={{ color: theme.palette.primary.main }} />
                                </Tooltip>
                              )
                            }
                            <TruncatedTextField
                              text={link.company_profile.company_name}
                              mode="characters"
                              limit={30}
                              showMoreEnabled={false}
                              variant="subtitle2"
                              style={{ textTransform: 'capitalize' }}
                            />
                          </Stack>

                        }
                        secondary={t(`connections.table.company_type.${link.company_profile.company_type}`)}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    <Divider variant="middle" component="li" />
                  </Fragment>
                ))}
              </List>

            </Stack>

          )
        }

      </CardContent>

      <ConfirmDialog
        open={addLinkControl.value}
        title={
          <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" gap={1} alignItems="center">
              <Iconify icon="material-symbols:dataset-linked-outline" />
              <Box>{t('contacts.detail.linked_companies.dialog_title')}</Box>
            </Stack>
            <Button variant="text" onClick={onAddNewCompany} startIcon={<Iconify icon="eva:plus-fill" />}>
              {t('common.new')}
            </Button>
          </Stack>
        }
        onClose={onLinkModalClose}
        content={
          <Autocomplete
            sx={{
              paddingTop: 2,
            }}
            multiple
            options={linkableCompanies as ICompanyProfile[] ?? []}
            value={selectedCompanies as ICompanyProfile[]}
            onInputChange={(_e, value) => onSearchQueryChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(
                  'contacts.detail.linked_companies.search'
                )}
                placeholder={t(
                  'contacts.detail.linked_companies.search_placeholder'
                )}
              />
            )}
            onChange={(_e, value: ICompanyProfile[]) => setSelectedCompanies(value)}
            renderOption={({ key, ...rest }, option: ICompanyProfile) => (
              <li key={key} {...rest}>
                <Stack direction="column">
                  <Typography variant="body1">
                    {`${option.company_name}`}
                  </Typography>
                </Stack>
              </li>
            )}
            getOptionLabel={(option) => option.company_name as string}
            isOptionEqualToValue={(option, value) =>
              option && (option as ICompanyProfile).id === (value as ICompanyProfile).id
            }
            getOptionKey={(option) => (option as ICompanyProfile).id as string}
          />
        }
        action={
          <Button variant="contained" disabled={!selectedCompanies.length} onClick={handleAddLinks}>
            {t('contacts.detail.linked_companies.associate')}
          </Button>
        }
      />
    </Card >
  )

}

export default LinkedCompaniesCard; 